import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { colorOf, DAYS, MONTHS, GRADE_GROUPS } from "@/lib/subjects";
import { ClipboardList, Plus, Trash2, Save, Check, Users } from "lucide-react";

const emptyDays = () => DAYS.map((d) => ({ day: d, date: "", label: "", entries: [] }));

const normalizeDays = (savedDays) => {
  const map = {};
  (savedDays || []).forEach((d) => { map[d.day] = d; });
  return DAYS.map((dayName) => map[dayName] || { day: dayName, date: "", label: "", entries: [] });
};

export default function AdminPlanEditor() {
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || "");
  const [week, setWeek] = useState(1);
  const [month, setMonth] = useState("");
  const [weekLabel, setWeekLabel] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [weeklyTarget, setWeeklyTarget] = useState(0);
  const [days, setDays] = useState(emptyDays());
  const [saving, setSaving] = useState(false);
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 3);
  const [clsSection, setClsSection] = useState(params.get("section") || "");

  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data));
    api.get("/subjects").then(({ data }) => setSubjects(data));
  }, []);

  const loadPlan = async (sid, wk) => {
    if (!sid) return;
    try {
      const { data } = await api.get("/weekly-plans", { params: { student_id: sid, week_number: wk } });
      if (data) {
        setWeekLabel(data.week_label || "");
        setMonth(data.month || "");
        setDateRange(data.date_range || "");
        setWeeklyTarget(data.weekly_target || 0);
        setDays(normalizeDays(data.days));
      } else {
        setWeekLabel(""); setDateRange(""); setWeeklyTarget(0); setDays(emptyDays());
      }
    } catch {
      setDays(emptyDays());
    }
  };

  useEffect(() => {
    if (studentId) loadPlan(studentId, week);
  }, [studentId, week]);

  const addEntry = (di) => {
    const sub = subjects[0];
    if (!sub) return toast.error("Önce ders tanımlayın");
    setDays((prev) => {
      const next = [...prev];
      next[di] = { ...next[di], entries: [...next[di].entries, { subject_id: sub.id, subject_name: sub.name, color: sub.color, topic: "", question_count: 20, note: "", completed: false }] };
      return next;
    });
  };

  const updateEntry = (di, ei, patch) => {
    setDays((prev) => {
      const next = [...prev];
      const entries = [...next[di].entries];
      entries[ei] = { ...entries[ei], ...patch };
      next[di] = { ...next[di], entries };
      return next;
    });
  };

  const removeEntry = (di, ei) => {
    setDays((prev) => {
      const next = [...prev];
      next[di] = { ...next[di], entries: next[di].entries.filter((_, i) => i !== ei) };
      return next;
    });
  };

  const setDayField = (di, patch) => {
    setDays((prev) => {
      const next = [...prev];
      next[di] = { ...next[di], ...patch };
      return next;
    });
  };

  const pickSubject = (di, ei, subjectId) => {
    const sub = subjects.find((s) => s.id === subjectId);
    if (sub) updateEntry(di, ei, { subject_id: sub.id, subject_name: sub.name, color: sub.color });
  };

  const save = async () => {
    if (!studentId) return toast.error("Öğrenci seçin");
    setSaving(true);
    try {
      await api.post("/weekly-plans", {
        student_id: studentId,
        week_number: Number(week),
        week_label: weekLabel,
        month,
        date_range: dateRange,
        weekly_target: Number(weeklyTarget),
        days,
      });
      toast.success("Plan kaydedildi ✓");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const applyClass = async () => {
    if (!window.confirm(`${clsGrade}. Sınıf${clsSection ? " / " + clsSection : " (tüm şubeler)"} ${week}. Hafta planı güncellenecek. Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post("/weekly-plans/assign-class", {
        grade: clsGrade,
        section: clsSection || null,
        week_number: Number(week),
        week_label: weekLabel,
        month,
        date_range: dateRange,
        weekly_target: Number(weeklyTarget),
        days,
      });
      toast.success(`${data.count} öğrenciye ${week}. Hafta planı uygulandı ✓`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
            <ClipboardList className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Haftalık Plan Düzenle</h1>
            <p className="text-slate-500">Öğrenci ve hafta seçip günlük görevleri girin — anında öğrenci ekranına yansır.</p>
          </div>
        </div>
        <button data-testid="save-plan-btn" onClick={save} disabled={saving} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-700 active:scale-95 disabled:opacity-60">
          <Save className="h-5 w-5" /> {saving ? "Kaydediliyor…" : "Planı Kaydet"}
        </button>
      </div>

      {/* Sınıfa Toplu Uygula Barı */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5">
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Sınıf</label>
          <select data-testid="plan-class-grade" value={clsGrade} onChange={(e) => setClsGrade(Number(e.target.value))} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400">
            {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Şube</label>
          <input data-testid="plan-class-section" value={clsSection} onChange={(e) => setClsSection(e.target.value)} placeholder="Tüm şubeler" className="w-32 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400" />
        </div>
        <button data-testid="plan-apply-class-btn" onClick={applyClass} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
          <Users className="h-5 w-5" /> Tüm Sınıfa Uygula
        </button>
        <p className="text-xs text-slate-500">Bu haftanın planını seçili sınıftaki tüm öğrencilere tek tıkla uygular.</p>
      </div>

      {/* Controls */}
      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci</label>
          <ClassStudentSelect students={students} value={studentId} onChange={(v) => setStudentId(v)} testid="plan-student-select" placeholder="Seçiniz…" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Hafta No</label>
          <input data-testid="plan-week-input" type="number" min={1} value={week} onChange={(e) => setWeek(Number(e.target.value) || 1)} className={ctrl} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Ay</label>
          <select data-testid="plan-month-select" value={month} onChange={(e) => setMonth(e.target.value)} className={ctrl}>
            <option value="">Ay seç…</option>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Tarih Aralığı</label>
          <input data-testid="plan-daterange-input" value={dateRange} onChange={(e) => setDateRange(e.target.value)} placeholder="18-24 Eylül" className={ctrl} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Haftalık Hedef</label>
          <input data-testid="plan-target-input" type="number" min={0} value={weeklyTarget} onChange={(e) => setWeeklyTarget(Number(e.target.value) || 0)} className={ctrl} />
        </div>
        <div className="sm:col-span-2 lg:col-span-6">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Hafta Başlığı (opsiyonel)</label>
          <input data-testid="plan-label-input" value={weekLabel} onChange={(e) => setWeekLabel(e.target.value)} placeholder="ör. Tekrar Haftası" className={ctrl} />
        </div>
      </div>

      {!studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">
          Düzenlemeye başlamak için yukarıdan bir öğrenci seçin.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {days.map((d, di) => (
            <div key={d.day} data-testid={`editor-day-${di}`} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="font-display text-xl font-bold text-slate-900">{d.day}</h3>
                <div className="flex gap-2">
                  <input value={d.date} onChange={(e) => setDayField(di, { date: e.target.value })} placeholder="09-18" className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-indigo-400" data-testid={`day-date-${di}`} />
                  <input value={d.label} onChange={(e) => setDayField(di, { label: e.target.value })} placeholder="etiket" className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-indigo-400" data-testid={`day-label-${di}`} />
                </div>
              </div>

              <div className="space-y-3">
                {d.entries.map((e, ei) => {
                  const c = colorOf(e.color);
                  return (
                    <div key={ei} className={`rounded-xl border ${c.border} ${c.bg} p-3 space-y-2`}>
                      <div className="flex items-center gap-2">
                        <select value={e.subject_id} onChange={(ev) => pickSubject(di, ei, ev.target.value)} className="flex-1 rounded-lg border border-white bg-white/70 px-2 py-1.5 text-sm font-semibold outline-none" data-testid={`entry-subject-${di}-${ei}`}>
                          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <button onClick={() => removeEntry(di, ei)} className="rounded-lg p-1.5 text-slate-500 hover:bg-white/60 hover:text-rose-600 active:scale-90" data-testid={`remove-entry-${di}-${ei}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <input value={e.topic} onChange={(ev) => updateEntry(di, ei, { topic: ev.target.value })} placeholder="Konu başlığı" className="w-full rounded-lg border border-white bg-white/70 px-2 py-1.5 text-sm outline-none" data-testid={`entry-topic-${di}-${ei}`} />
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} value={e.question_count} onChange={(ev) => updateEntry(di, ei, { question_count: Number(ev.target.value) || 0 })} className="w-24 rounded-lg border border-white bg-white/70 px-2 py-1.5 text-sm outline-none" data-testid={`entry-count-${di}-${ei}`} />
                        <span className="text-xs text-slate-500">soru</span>
                        <button onClick={() => updateEntry(di, ei, { completed: !e.completed })} className={`ml-auto flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold transition active:scale-95 ${e.completed ? "bg-emerald-600 text-white" : "bg-white/70 text-slate-600"}`} data-testid={`entry-complete-${di}-${ei}`}>
                          <Check className="h-3.5 w-3.5" /> {e.completed ? "Tamam" : "Bekliyor"}
                        </button>
                      </div>
                      <input value={e.note} onChange={(ev) => updateEntry(di, ei, { note: ev.target.value })} placeholder="Not (opsiyonel)" className="w-full rounded-lg border border-white bg-white/70 px-2 py-1.5 text-xs outline-none" data-testid={`entry-note-${di}-${ei}`} />
                    </div>
                  );
                })}
              </div>

              <button onClick={() => addEntry(di)} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-2.5 text-sm font-semibold text-slate-500 transition hover:border-indigo-400 hover:text-indigo-600 active:scale-95" data-testid={`add-entry-${di}`}>
                <Plus className="h-4 w-4" /> Ders / Görev Ekle
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ctrl = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
